/**
 * 同轴心与锁定标准配合求解器。
 *
 * 文件职责：
 * 1. 独立处理 Concentric Mate 和 Lock Mate 的刚体 transform 求解；
 * 2. 保持输入输出为纯数据，避免依赖 Viewer、Object3D、UI 或命令状态；
 * 3. 为主 `StandardMateSolver` 减少具体 Mate 类型细节，保持求解器门面职责清晰。
 */

import * as THREE from 'three';
import type { ComponentTransformSnapshot } from '../component-instance';
import {
  MateAlignment,
  MateConcentricMisalignmentType,
  MateEntityKind,
  MateSolveStatus,
  type AssemblyMateDefinition,
  type MateEntityRef,
} from '../definition';
import type {
  StandardMateApplyResult,
  StandardMateMovingSideResolution,
  AssemblyMateSolverWorldBounds,
} from './StandardMateSolverTypes';
import {
  applyWorldDeltaToTransform,
  composeMatrix,
  createRotationBetweenDirections,
  createRotationDeltaAroundPoint,
  createTranslationDelta,
  decomposeMatrix,
  resolveAxisGeometry,
  resolvePointGeometry,
  resolveSphereGeometry,
  type AxisGeometry,
} from './geometry/StandardMateGeometryAccess';
import {
  projectStandardMateWorldBoundsToAxisInterval,
  transformStandardMateWorldBounds,
} from './geometry/StandardMateWorldBounds';

/** 锁旋转残差判断复用的单位四元数，避免拖拽重求解路径重复创建对象。 */
const IDENTITY_QUATERNION = new THREE.Quaternion();

/** 临时同轴心预览的包围盒贴边间隙比例。 */
const CONCENTRIC_INITIAL_PLACEMENT_CLEARANCE_RATIO = 0.001;

/**
 * 第一条同轴心精确对齐后的中间解。
 */
interface BaseConcentricAlignmentResult {
  /** 第一条 Mate 对齐后的固定轴，方向已按当前实体语义选择稳定符号。 */
  readonly fixedAxis: AxisGeometry;
  /** 从输入姿态到第一条同轴心精确对齐姿态的世界空间 delta。 */
  readonly worldDelta: THREE.Matrix4;
  /** 第一条同轴心精确对齐后的移动组件位姿。 */
  readonly transform: ComponentTransformSnapshot;
}

/**
 * 同轴心求解选项。
 */
interface SolveConcentricMateOptions {
  /**
   * 是否允许用组件包围盒修正临时 Mate 的初始轴向位置。
   *
   * 说明：
   * 该选项只应在 PropertyManager 临时预览中启用；已提交同轴心仍必须保留沿轴滑动自由度。
   */
  readonly useInitialPlacementBounds?: boolean;
  /**
   * 圆柱孔/圆边同轴心是否按“保持当前朝向的最小旋转”求解轴向（忽略 alignment 符号）。
   *
   * 说明：
   * 仅对**已提交**同轴心启用。已提交圆柱孔同轴心的轴向符号来自 BRep 面朝向、不是用户可见约束，
   * 已按无向中心线校验通过；再求解（拖拽扰动等）若按 alignment 强推方向会把刚体翻转 180° 顶破其它配合。
   * 瞬态预览（新建/切换对齐）不启用，仍尊重用户的同向/反向选择以产生可见翻转。
   */
  readonly preserveRoundHoleOrientation?: boolean;
}

/**
 * 判断实体是否为孔轴类圆形实体。
 *
 * @param entity Mate 实体引用。
 * @returns 是否为轴向符号不可作为约束语义的圆形实体。
 */
function isRoundConcentricAxisEntity(entity: MateEntityRef): boolean {
  return entity.entityKind === MateEntityKind.Cylinder
    || entity.entityKind === MateEntityKind.CircularEdge;
}

/**
 * 判断同轴心 Mate 是否应把轴向当作无向中心线。
 *
 * 设计说明：
 * SOLIDWORKS 对圆柱孔/圆边 Concentric 的核心语义是共享中心线。BRep 面的
 * `axisDirection` 符号来自拓扑面朝向，同一刚体上的不同孔可能一正一反；若把该符号当成
 * 必须满足的自由度，会把可解的双孔同轴心误报为 over-defined。
 *
 * @param mate 同轴心 Mate。
 * @returns 是否忽略轴向正反。
 */
function shouldUseUnsignedConcentricAxisDirection(mate: AssemblyMateDefinition): boolean {
  return isRoundConcentricAxisEntity(mate.firstEntity)
    && isRoundConcentricAxisEntity(mate.secondEntity);
}

/**
 * 从组件 transform 快照读取旋转四元数。
 *
 * @param transform 组件 transform 快照。
 * @returns 归一化后的旋转四元数。
 */
function quaternionFromTransformSnapshot(transform: ComponentTransformSnapshot): THREE.Quaternion {
  return new THREE.Quaternion(
    transform.quaternion[0],
    transform.quaternion[1],
    transform.quaternion[2],
    transform.quaternion[3],
  ).normalize();
}

/**
 * 根据两个方向创建旋转 delta 矩阵。
 *
 * @param origin 旋转中心。
 * @param from 源方向。
 * @param to 目标方向。
 * @param epsilon 容差。
 * @returns 旋转 delta；退化时返回 null。
 */
function createRotationDeltaBetweenDirections(
  origin: THREE.Vector3,
  from: THREE.Vector3,
  to: THREE.Vector3,
  epsilon: number,
): THREE.Matrix4 | null {
  const rotation = createRotationBetweenDirections(from, to, epsilon);
  if (!rotation) {
    return null;
  }
  return createRotationDeltaAroundPoint(origin, rotation);
}

/**
 * 将点投影到轴线上。
 *
 * @param point 待投影点。
 * @param axis 目标轴线。
 * @returns 投影点。
 */
function projectPointToAxis(point: THREE.Vector3, axis: AxisGeometry): THREE.Vector3 {
  const originToPoint = point.clone().sub(axis.origin);
  return axis.origin.clone().addScaledVector(axis.direction, originToPoint.dot(axis.direction));
}

/**
 * 解析临时同轴心预览的轴向避让平移。
 *
 * 设计说明：
 * Concentric Mate 本身不能约束沿公共轴的平移；但新建预览若只做径向对齐，
 * 第二选择零件容易直接落入第一选择零件体内。这里仅在临时 Mate 上使用 world AABB
 * 选择最近的非重叠轴向位置。即使两个包围盒在轴向已经离得很远，也会拉回到第一选择
 * 包围盒的最近一侧，避免初始预览距离过大；提交后的同轴心仍保留完整轴向自由度。
 *
 * @param input 避让输入。
 * @returns 需要额外应用的世界平移；无需避让时返回 null。
 */
function resolveConcentricInitialPlacementTranslation(input: {
  /** 固定侧当前世界包围盒。 */
  readonly fixedBounds: AssemblyMateSolverWorldBounds | undefined;
  /** 移动侧当前世界包围盒。 */
  readonly movingBounds: AssemblyMateSolverWorldBounds | undefined;
  /** 移动侧完成同轴径向对齐后的世界 delta。 */
  readonly movingAlignmentDelta: THREE.Matrix4;
  /** 公共轴方向。 */
  readonly axisDirection: THREE.Vector3;
  /** 数值容差。 */
  readonly epsilon: number;
}): THREE.Vector3 | null {
  if (!input.fixedBounds || !input.movingBounds) {
    return null;
  }

  const axisLength = input.axisDirection.length();
  if (!Number.isFinite(axisLength) || axisLength <= input.epsilon) {
    return null;
  }
  const axisDirection = input.axisDirection.clone().multiplyScalar(1 / axisLength);
  const alignedMovingBounds = transformStandardMateWorldBounds(input.movingBounds, input.movingAlignmentDelta);
  const fixedInterval = projectStandardMateWorldBoundsToAxisInterval(input.fixedBounds, axisDirection);
  const movingInterval = projectStandardMateWorldBoundsToAxisInterval(alignedMovingBounds, axisDirection);
  const clearance = Math.max(
    input.epsilon * 10,
    Math.min(fixedInterval.length, movingInterval.length) * CONCENTRIC_INITIAL_PLACEMENT_CLEARANCE_RATIO,
  );
  const positiveSideOffset = fixedInterval.max + clearance - movingInterval.min;
  const negativeSideOffset = fixedInterval.min - clearance - movingInterval.max;
  const axialOffset = Math.abs(positiveSideOffset) <= Math.abs(negativeSideOffset)
    ? positiveSideOffset
    : negativeSideOffset;
  if (Math.abs(axialOffset) <= input.epsilon) {
    return null;
  }

  return axisDirection.multiplyScalar(axialOffset);
}

/**
 * 计算两条轴线之间的最短距离。
 *
 * @param first 第一条轴线。
 * @param second 第二条轴线。
 * @param epsilon 容差。
 * @returns 轴线最短距离。
 */
function calculateAxisAxisDistance(
  first: AxisGeometry,
  second: AxisGeometry,
  epsilon: number,
): number {
  const cross = first.direction.clone().cross(second.direction);
  const delta = second.origin.clone().sub(first.origin);
  if (cross.lengthSq() <= epsilon * epsilon) {
    return delta.sub(first.direction.clone().multiplyScalar(delta.dot(first.direction))).length();
  }
  return Math.abs(delta.dot(cross.normalize()));
}

/**
 * 按同轴心对齐方向解析移动轴应达到的目标方向。
 *
 * @param mate 同轴心 Mate。
 * @param fixedAxis 固定侧轴线。
 * @returns 移动轴目标方向。
 */
function resolveDirectedConcentricTargetDirection(
  mate: AssemblyMateDefinition,
  fixedAxis: AxisGeometry,
): THREE.Vector3 {
  return mate.alignment === MateAlignment.AntiAligned
    ? fixedAxis.direction.clone().multiplyScalar(-1)
    : fixedAxis.direction.clone();
}

/**
 * 按同轴心实体语义解析移动轴应达到的目标方向。
 *
 * @param mate 同轴心 Mate。
 * @param fixedAxis 固定侧轴线。
 * @param movingAxis 移动侧轴线；圆形实体会用它选择最小旋转方向。
 * @returns 移动轴目标方向。
 */
function resolveConcentricTargetDirection(
  mate: AssemblyMateDefinition,
  fixedAxis: AxisGeometry,
  movingAxis?: AxisGeometry,
  preserveRoundHoleOrientation?: boolean,
): THREE.Vector3 {
  const directedTarget = resolveDirectedConcentricTargetDirection(mate, fixedAxis);
  /**
   * 求解阶段默认尊重用户选择的同向/反向对齐，让 PropertyManager 新建/切换对齐（瞬态预览）能实际翻转组件。
   * 但对**已提交**的圆柱孔/圆边同轴心，轴向符号不是用户可见约束（BRep 面朝向决定，最终按无向中心线校验）：
   * 此时若仍按 alignment 强推方向，一旦已提交位姿是反向平行（合法、已通过无向校验），再求解（如拖拽扰动）
   * 会把整块刚体强行翻转 180°、顶破其它内部配合。因此已提交的圆柱孔同轴心改按“最小旋转”保持当前朝向。
   */
  if (!preserveRoundHoleOrientation || !movingAxis || !shouldUseUnsignedConcentricAxisDirection(mate)) {
    return directedTarget;
  }
  const reversedTarget = directedTarget.clone().multiplyScalar(-1);
  return movingAxis.direction.angleTo(directedTarget) <= movingAxis.direction.angleTo(reversedTarget)
    ? directedTarget
    : reversedTarget;
}

/**
 * 判断同轴心轴向是否满足当前实体语义。
 *
 * @param mate 同轴心 Mate。
 * @param fixedAxis 固定轴线。
 * @param movingAxis 移动轴线。
 * @param epsilon 容差。
 * @returns 是否满足轴向要求。
 */
function isConcentricAxisDirectionSatisfied(
  mate: AssemblyMateDefinition,
  fixedAxis: AxisGeometry,
  movingAxis: AxisGeometry,
  epsilon: number,
): boolean {
  if (shouldUseUnsignedConcentricAxisDirection(mate)) {
    return areAxesParallel(fixedAxis, movingAxis, epsilon);
  }
  return areDirectionsAligned(
    movingAxis.direction,
    resolveConcentricTargetDirection(mate, fixedAxis),
    epsilon,
  );
}

/**
 * 判断两个方向是否按有向语义对齐。
 *
 * @param first 第一方向。
 * @param second 第二方向。
 * @param epsilon 容差。
 * @returns 是否对齐。
 */
function areDirectionsAligned(
  first: THREE.Vector3,
  second: THREE.Vector3,
  epsilon: number,
): boolean {
  return first.angleTo(second) <= Math.max(epsilon, 1e-7);
}

/**
 * 判断两条轴线是否平行，忽略方向正反。
 *
 * @param first 第一条轴线。
 * @param second 第二条轴线。
 * @param epsilon 容差。
 * @returns 是否平行。
 */
function areAxesParallel(
  first: AxisGeometry,
  second: AxisGeometry,
  epsilon: number,
): boolean {
  return first.direction.clone().cross(second.direction).length() <= Math.max(epsilon, 1e-7);
}

/**
 * 按世界空间 delta 变换轴线。
 *
 * @param axis 原始轴线。
 * @param delta 世界空间 delta。
 * @returns 变换后的轴线。
 */
function transformAxisGeometry(axis: AxisGeometry, delta: THREE.Matrix4): AxisGeometry {
  return {
    origin: axis.origin.clone().applyMatrix4(delta),
    direction: axis.direction.clone().transformDirection(delta).normalize(),
    radius: axis.radius,
  };
}

/**
 * 解析目标轴线相对基准轴线的垂直偏移向量。
 *
 * @param axis 目标轴线。
 * @param baseAxis 基准轴线。
 * @returns 目标轴线原点到基准轴线的垂直偏移。
 */
function resolveAxisOffsetFromBaseAxis(
  axis: AxisGeometry,
  baseAxis: AxisGeometry,
): THREE.Vector3 {
  return axis.origin.clone().sub(projectPointToAxis(axis.origin, baseAxis));
}

/**
 * 判断变换后的移动轴是否满足指定同轴心 Mate。
 *
 * @param mate 同轴心 Mate。
 * @param fixedAxis 固定轴线。
 * @param movingAxis 变换后的移动轴线。
 * @param epsilon 容差。
 * @returns 是否满足。
 */
function isConcentricAxisSatisfied(
  mate: AssemblyMateDefinition,
  fixedAxis: AxisGeometry,
  movingAxis: AxisGeometry,
  epsilon: number,
): boolean {
  return isConcentricAxisDirectionSatisfied(mate, fixedAxis, movingAxis, epsilon)
    && calculateAxisAxisDistance(fixedAxis, movingAxis, epsilon) <= epsilon;
}

/**
 * 按单条同轴心 Mate 先求出基础对齐位姿。
 *
 * @param mate 同轴心 Mate。
 * @param side 移动侧。
 * @param epsilon 容差。
 * @returns 基础对齐结果；轴线退化或无法旋转时返回 null。
 */
function solveBaseConcentricAlignment(
  mate: AssemblyMateDefinition,
  side: StandardMateMovingSideResolution,
  epsilon: number,
): BaseConcentricAlignmentResult | null {
  const fixedAxis = resolveAxisGeometry(side.fixedEntity);
  const movingAxis = resolveAxisGeometry(side.movingEntity);
  if (!fixedAxis || !movingAxis) {
    return null;
  }

  const targetDirection = resolveConcentricTargetDirection(mate, fixedAxis);
  const rotationDelta = createRotationDeltaBetweenDirections(
    movingAxis.origin,
    movingAxis.direction,
    targetDirection,
    epsilon,
  );
  if (!rotationDelta) {
    return null;
  }

  const alignedFixedAxis = {
    origin: fixedAxis.origin.clone(),
    direction: targetDirection.normalize(),
    radius: fixedAxis.radius,
  };
  const rotatedOrigin = movingAxis.origin.clone().applyMatrix4(rotationDelta);
  const projectedOrigin = projectPointToAxis(rotatedOrigin, alignedFixedAxis);
  const translationDelta = createTranslationDelta(projectedOrigin.sub(rotatedOrigin));
  const worldDelta = translationDelta.multiply(rotationDelta);

  return {
    fixedAxis: alignedFixedAxis,
    worldDelta,
    transform: applyWorldDeltaToTransform(side.movingComponent.transform, worldDelta),
  };
}

/**
 * 移除同轴心错位策略，按普通 Concentric Mate 计算精确解。
 *
 * @param mate 原始 Mate。
 * @returns 不携带错位策略的 Mate。
 */
function withoutConcentricMisalignment(
  mate: AssemblyMateDefinition,
): AssemblyMateDefinition {
  return {
    ...mate,
    concentricMisalignment: undefined,
  };
}

/**
 * 在两个刚体 transform 解之间做中点插值。
 *
 * 设计说明：
 * SOLIDWORKS 的 Symmetric misaligned concentric 会把两条同轴心配合的偏差平均分摊。
 * 当前前端刚体求解器没有全局自由度优化器，因此这里把“当前 Mate 精确解”和“链接 Mate 精确解”
 * 转换成同一移动组件的两个目标 transform，再取 50% 插值，得到稳定且可验证的平均偏差位姿。
 *
 * @param first 第一条精确解。
 * @param second 第二条精确解。
 * @returns 平均后的 transform。
 */
function interpolateTransformHalfway(
  first: ComponentTransformSnapshot,
  second: ComponentTransformSnapshot,
): ComponentTransformSnapshot {
  const firstQuaternion = quaternionFromTransformSnapshot(first);
  const secondQuaternion = quaternionFromTransformSnapshot(second);
  firstQuaternion.slerp(secondQuaternion, 0.5).normalize();

  return {
    position: [
      (first.position[0] + second.position[0]) / 2,
      (first.position[1] + second.position[1]) / 2,
      (first.position[2] + second.position[2]) / 2,
    ],
    quaternion: [
      firstQuaternion.x,
      firstQuaternion.y,
      firstQuaternion.z,
      firstQuaternion.w,
    ],
    scale: [
      (first.scale[0] + second.scale[0]) / 2,
      (first.scale[1] + second.scale[1]) / 2,
      (first.scale[2] + second.scale[2]) / 2,
    ],
  };
}

/**
 * 解析同轴心配合中可按中心点处理的几何。
 *
 * 设计说明：
 * SOLIDWORKS 的 Concentric 不只约束圆柱轴线，也可以让球心、点与轴线/中心点重合。
 * 这里把球面归一为球心点，避免兼容性表放开的 Sphere 组合进入轴-轴求解后返回 unsupported。
 *
 * @param entity 配合实体。
 * @returns 中心点；无法按中心点处理时返回 null。
 */
function resolveConcentricPoint(entity: AssemblyMateDefinition['firstEntity']): THREE.Vector3 | null {
  const point = resolvePointGeometry(entity);
  if (point) {
    return point.point;
  }

  const sphere = resolveSphereGeometry(entity);
  return sphere ? sphere.center : null;
}

/**
 * 创建平移求解结果。
 *
 * @param side 当前移动侧。
 * @param translation 世界空间平移量。
 * @returns 求解结果。
 */
function createConcentricTranslationResult(
  side: StandardMateMovingSideResolution,
  translation: THREE.Vector3,
): StandardMateApplyResult {
  return {
    status: MateSolveStatus.Solved,
    movedComponentInstanceId: side.movingComponent.componentInstanceId,
    transform: applyWorldDeltaToTransform(side.movingComponent.transform, createTranslationDelta(translation)),
  };
}

/**
 * 按选择瞬间的相对姿态求解同轴心锁旋转。
 *
 * 设计说明：
 * SOLIDWORKS 的 Concentric Lock rotation 只移除公共轴上的旋转自由度。MateEntityRef 保存了选择瞬间
 * 两个组件的纯数据 transform，因此这里可以在不依赖 Object3D 的前提下恢复“固定侧当前姿态 -> 移动侧
 * 选择瞬间相对姿态”的目标旋转，再围绕公共轴补偿旋转。
 *
 * @param side 当前移动侧解析结果。
 * @param alignedTransform 已完成同轴心轴线重合后的移动组件 transform。
 * @param alignedAxisOrigin 已完成轴线重合后的移动轴上一点。
 * @param epsilon 数值容差。
 * @returns 锁旋转后的 transform；缺少选择基线时返回 null。
 */
function solveConcentricLockRotationTransform(
  side: StandardMateMovingSideResolution,
  alignedTransform: ComponentTransformSnapshot,
  alignedAxisOrigin: THREE.Vector3,
  epsilon: number,
): ComponentTransformSnapshot | null {
  const fixedSelectionTransform = side.fixedEntity.componentTransformAtSelection;
  const movingSelectionTransform = side.movingEntity.componentTransformAtSelection;
  if (!fixedSelectionTransform || !movingSelectionTransform) {
    return null;
  }

  const fixedSelectionQuaternion = quaternionFromTransformSnapshot(fixedSelectionTransform);
  const movingSelectionQuaternion = quaternionFromTransformSnapshot(movingSelectionTransform);
  const fixedCurrentQuaternion = quaternionFromTransformSnapshot(side.fixedComponent.transform);
  const movingCurrentQuaternion = quaternionFromTransformSnapshot(alignedTransform);
  const selectionRelativeQuaternion = fixedSelectionQuaternion.clone().invert()
    .multiply(movingSelectionQuaternion)
    .normalize();
  const targetMovingQuaternion = fixedCurrentQuaternion.clone()
    .multiply(selectionRelativeQuaternion)
    .normalize();
  const rotationCorrection = targetMovingQuaternion
    .multiply(movingCurrentQuaternion.clone().invert())
    .normalize();

  /**
   * 当同轴心求解已经满足锁旋转目标时，不再创建额外矩阵，减少拖拽重求解路径上的浮点抖动。
   */
  if (rotationCorrection.angleTo(IDENTITY_QUATERNION) <= epsilon) {
    return alignedTransform;
  }

  return applyWorldDeltaToTransform(
    alignedTransform,
    createRotationDeltaAroundPoint(alignedAxisOrigin, rotationCorrection),
  );
}

/**
 * 求解轴线同轴。
 *
 * @param mate 配合定义。
 * @param side 移动侧。
 * @param epsilon 容差。
 * @param options 同轴心求解选项。
 * @returns 应用结果。
 */
export function solveConcentricMate(
  mate: AssemblyMateDefinition,
  side: StandardMateMovingSideResolution,
  epsilon: number,
  options: SolveConcentricMateOptions = {},
): StandardMateApplyResult {
  if (mate.concentricMisalignment?.type === MateConcentricMisalignmentType.AlignLinkedMate) {
    /**
     * SOLIDWORKS 的 Align linked mate 语义是“链接 Mate 精确同轴，当前 Mate 接受偏差”。
     * 因此当前 Mate 不应再推动组件 transform；最终偏差是否超过 maximum deviation 交给
     * validator 判定，避免第二条同轴心把第一条已经满足的同轴心重新拉坏。
     */
    return {
      status: MateSolveStatus.Solved,
      message: '同轴心错位配合按链接 Mate 精确求解，当前 Mate 仅校验最大偏差。',
    };
  }

  const targetAxis = resolveAxisGeometry(side.fixedEntity);
  const movingAxis = resolveAxisGeometry(side.movingEntity);
  const targetPoint = resolveConcentricPoint(side.fixedEntity);
  const movingPoint = resolveConcentricPoint(side.movingEntity);

  if (targetAxis && movingAxis) {
    const targetDirection = resolveConcentricTargetDirection(
      mate,
      targetAxis,
      movingAxis,
      options.preserveRoundHoleOrientation,
    );
    const rotationDelta = createRotationDeltaBetweenDirections(
      movingAxis.origin,
      movingAxis.direction,
      targetDirection,
      epsilon,
    );
    if (!rotationDelta) {
      return {
        status: MateSolveStatus.Unsolved,
        message: '轴向对齐失败。',
      };
    }

    const rotatedTransform = applyWorldDeltaToTransform(side.movingComponent.transform, rotationDelta);
    const rotatedOrigin = movingAxis.origin.clone().applyMatrix4(rotationDelta);
    /**
     * 同轴心只约束两条轴线重合，不约束沿公共轴的自由滑动。
     * 因此这里把移动轴上一点投影到目标轴，而不是强行移动到目标轴原点。
     */
    const projectedOrigin = projectPointToAxis(rotatedOrigin, targetAxis);
    const translation = projectedOrigin.clone().sub(rotatedOrigin);
    const initialPlacementTranslation = options.useInitialPlacementBounds
      ? resolveConcentricInitialPlacementTranslation({
          fixedBounds: side.fixedComponent.worldBounds,
          movingBounds: side.movingComponent.worldBounds,
          movingAlignmentDelta: createTranslationDelta(translation).multiply(rotationDelta.clone()),
          axisDirection: targetAxis.direction,
          epsilon,
        })
      : null;
    const translated = initialPlacementTranslation
      ? applyWorldDeltaToTransform(
          rotatedTransform,
          createTranslationDelta(translation.clone().add(initialPlacementTranslation)),
        )
      : applyWorldDeltaToTransform(rotatedTransform, createTranslationDelta(translation));
    const translatedAxisOrigin = rotatedOrigin.clone().add(translation);
    if (initialPlacementTranslation) {
      translatedAxisOrigin.add(initialPlacementTranslation);
    }
    const lockedTransform = mate.lockRotation
      ? solveConcentricLockRotationTransform(side, translated, translatedAxisOrigin, epsilon)
      : translated;
    if (!lockedTransform) {
      return {
        status: MateSolveStatus.MissingReference,
        message: '同轴心锁定旋转缺少选择瞬间的组件姿态基线。',
      };
    }

    return {
      status: MateSolveStatus.Solved,
      movedComponentInstanceId: side.movingComponent.componentInstanceId,
      transform: lockedTransform,
    };
  }

  if (targetPoint && movingPoint) {
    return createConcentricTranslationResult(
      side,
      targetPoint.clone().sub(movingPoint),
    );
  }

  if (targetAxis && movingPoint) {
    return createConcentricTranslationResult(
      side,
      projectPointToAxis(movingPoint, targetAxis).sub(movingPoint),
    );
  }

  if (targetPoint && movingAxis) {
    const projectedPoint = projectPointToAxis(targetPoint, movingAxis);
    return createConcentricTranslationResult(
      side,
      targetPoint.clone().sub(projectedPoint),
    );
  }

  return {
    status: MateSolveStatus.UnsupportedGeometry,
    message: '同轴心配合需要可解析轴线、球心或点。',
  };
}

/**
 * 求解同一组件对上的两条精确同轴心配合。
 *
 * 设计说明：
 * 1. 第一条同轴心会保留沿公共轴平移与绕公共轴旋转两个自由度；
 * 2. 第二条平行孔同轴心应消耗绕公共轴旋转自由度，而不是用普通横向平移破坏第一条同轴心；
 * 3. 该函数只处理两组平行轴线的精确 Concentric 组合，孔距不一致时返回不可解，错位同轴心仍走专用策略。
 *
 * @param baseMate 已作为剩余自由度基准的同轴心 Mate。
 * @param baseSide baseMate 的移动侧。
 * @param secondaryMate 需要在剩余自由度内满足的第二条同轴心 Mate。
 * @param secondarySide secondaryMate 的移动侧。
 * @param epsilon 容差。
 * @returns 组合求解结果；非平行轴线组合返回 null 交由普通求解器处理。
 */
export function solveConcentricMatePair(
  baseMate: AssemblyMateDefinition,
  baseSide: StandardMateMovingSideResolution,
  secondaryMate: AssemblyMateDefinition,
  secondarySide: StandardMateMovingSideResolution,
  epsilon: number,
): StandardMateApplyResult | null {
  if (baseSide.movingComponent.componentInstanceId !== secondarySide.movingComponent.componentInstanceId) {
    return null;
  }

  const baseMovingAxis = resolveAxisGeometry(baseSide.movingEntity);
  const secondaryFixedAxis = resolveAxisGeometry(secondarySide.fixedEntity);
  const secondaryMovingAxis = resolveAxisGeometry(secondarySide.movingEntity);
  if (!baseMovingAxis || !secondaryFixedAxis || !secondaryMovingAxis) {
    return null;
  }

  const baseAlignment = solveBaseConcentricAlignment(baseMate, baseSide, epsilon);
  if (!baseAlignment) {
    return null;
  }

  const transformedBaseMovingAxis = transformAxisGeometry(baseMovingAxis, baseAlignment.worldDelta);
  const transformedSecondaryMovingAxis = transformAxisGeometry(secondaryMovingAxis, baseAlignment.worldDelta);

  if (
    !areAxesParallel(baseAlignment.fixedAxis, secondaryFixedAxis, epsilon)
    || !areAxesParallel(baseAlignment.fixedAxis, transformedSecondaryMovingAxis, epsilon)
    || !isConcentricAxisDirectionSatisfied(
      secondaryMate,
      secondaryFixedAxis,
      transformedSecondaryMovingAxis,
      epsilon,
    )
  ) {
    return null;
  }

  const fixedOffset = resolveAxisOffsetFromBaseAxis(secondaryFixedAxis, baseAlignment.fixedAxis);
  const movingOffset = resolveAxisOffsetFromBaseAxis(transformedSecondaryMovingAxis, baseAlignment.fixedAxis);
  const fixedOffsetLength = fixedOffset.length();
  const movingOffsetLength = movingOffset.length();
  if (Math.abs(fixedOffsetLength - movingOffsetLength) > epsilon) {
    return {
      status: MateSolveStatus.Unsolved,
      message: '两条同轴心配合的孔距不一致，无法在第一条同轴心剩余自由度内同时满足。',
    };
  }

  if (fixedOffsetLength <= epsilon || movingOffsetLength <= epsilon) {
    if (
      isConcentricAxisSatisfied(baseMate, baseAlignment.fixedAxis, transformedBaseMovingAxis, epsilon)
      && isConcentricAxisSatisfied(secondaryMate, secondaryFixedAxis, transformedSecondaryMovingAxis, epsilon)
    ) {
      return {
        status: MateSolveStatus.Solved,
        movedComponentInstanceId: baseSide.movingComponent.componentInstanceId,
        transform: baseAlignment.transform,
      };
    }
    return {
      status: MateSolveStatus.Unsolved,
      message: '第二条同轴心与第一条同轴心共线或退化，无法解析稳定的绕轴旋转解。',
    };
  }

  const normalizedMovingOffset = movingOffset.clone().multiplyScalar(1 / movingOffsetLength);
  const normalizedFixedOffset = fixedOffset.clone().multiplyScalar(1 / fixedOffsetLength);
  const rotationAngle = Math.atan2(
    baseAlignment.fixedAxis.direction.dot(normalizedMovingOffset.clone().cross(normalizedFixedOffset)),
    normalizedMovingOffset.dot(normalizedFixedOffset),
  );
  const correctionRotation = new THREE.Quaternion().setFromAxisAngle(baseAlignment.fixedAxis.direction, rotationAngle);
  const correctionDelta = createRotationDeltaAroundPoint(baseAlignment.fixedAxis.origin, correctionRotation);
  const combinedDelta = correctionDelta.clone().multiply(baseAlignment.worldDelta);
  const finalBaseMovingAxis = transformAxisGeometry(baseMovingAxis, combinedDelta);
  const finalSecondaryMovingAxis = transformAxisGeometry(secondaryMovingAxis, combinedDelta);
  if (
    !isConcentricAxisSatisfied(baseMate, baseAlignment.fixedAxis, finalBaseMovingAxis, epsilon)
    || !isConcentricAxisSatisfied(secondaryMate, secondaryFixedAxis, finalSecondaryMovingAxis, epsilon)
  ) {
    return {
      status: MateSolveStatus.Unsolved,
      message: '两条同轴心配合无法在同一刚体位姿下同时满足。',
    };
  }

  return {
    status: MateSolveStatus.Solved,
    movedComponentInstanceId: baseSide.movingComponent.componentInstanceId,
    transform: applyWorldDeltaToTransform(baseSide.movingComponent.transform, combinedDelta),
  };
}

/**
 * 求解 SOLIDWORKS Symmetric 同轴心错位配合。
 *
 * @param mate 当前带 Symmetric 策略的 Concentric Mate。
 * @param linkedMate 与当前 Mate 组成错位组的另一条 Concentric Mate。
 * @param side 当前 Mate 的移动侧。
 * @param linkedSide linked Mate 的移动侧。
 * @param epsilon 数值容差。
 * @returns 平均分摊偏差后的求解结果。
 */
export function solveSymmetricMisalignedConcentricMate(
  mate: AssemblyMateDefinition,
  linkedMate: AssemblyMateDefinition,
  side: StandardMateMovingSideResolution,
  linkedSide: StandardMateMovingSideResolution,
  epsilon: number,
): StandardMateApplyResult {
  const currentExactResult = solveConcentricMate(
    withoutConcentricMisalignment(mate),
    side,
    epsilon,
  );
  const linkedExactResult = solveConcentricMate(
    withoutConcentricMisalignment(linkedMate),
    linkedSide,
    epsilon,
  );
  if (
    currentExactResult.status !== MateSolveStatus.Solved
    || linkedExactResult.status !== MateSolveStatus.Solved
    || !currentExactResult.transform
    || !linkedExactResult.transform
    || !currentExactResult.movedComponentInstanceId
    || !linkedExactResult.movedComponentInstanceId
  ) {
    return {
      status: MateSolveStatus.Unsolved,
      message: '同轴心错位对称分摊需要当前 Mate 与 linked Mate 都可精确求解。',
    };
  }
  if (currentExactResult.movedComponentInstanceId !== linkedExactResult.movedComponentInstanceId) {
    return {
      status: MateSolveStatus.UnsupportedGeometry,
      message: '同轴心错位对称分摊暂只支持同一移动组件的链接 Mate 组。',
    };
  }

  return {
    status: MateSolveStatus.Solved,
    movedComponentInstanceId: currentExactResult.movedComponentInstanceId,
    transform: interpolateTransformHalfway(linkedExactResult.transform, currentExactResult.transform),
  };
}

/**
 * 求解锁定配合。
 *
 * @param mate 配合定义。
 * @param side 移动侧。
 * @returns 应用结果。
 */
export function solveLockMate(
  mate: AssemblyMateDefinition,
  side: StandardMateMovingSideResolution,
): StandardMateApplyResult {
  if (!mate.lockedRelativeTransform) {
    return {
      status: MateSolveStatus.MissingReference,
      message: '锁定配合缺少相对位姿基线，无法恢复 SOLIDWORKS Lock Mate 的完整约束。',
    };
  }

  const fixedMatrix = composeMatrix(side.fixedComponent.transform);
  const relativeMatrix = composeMatrix(mate.lockedRelativeTransform.transform);
  const nextMatrix = side.movingFirst
    ? fixedMatrix.multiply(relativeMatrix.clone().invert())
    : fixedMatrix.multiply(relativeMatrix);

  return {
    status: MateSolveStatus.Solved,
    movedComponentInstanceId: side.movingComponent.componentInstanceId,
    transform: {
      ...decomposeMatrix(nextMatrix),
      // 锁定配合只重建位姿，零件缩放保持自身原值，避免 decompose 累积缩放漂移。
      scale: [...side.movingComponent.transform.scale] as readonly [number, number, number],
    },
  };
}
